import { useScrollReveal } from "@/hooks/useScrollReveal";
import SectionTitle from "./SectionTitle";

const constructionTimeline = [
  {
    time: "8:30",
    title: "現場に直行",
    desc: "事前に現場へ直接向かいます",
  },
  {
    time: "9:00",
    title: "朝礼・安全確認後、業務開始",
    desc: "現場でチーム全員で朝礼を行い、本日の作業計画と安全確認を実施。その後、業務を開始します。※2〜3時間に一度、15〜20分の休憩あり",
  },
  {
    time: "12:00",
    title: "お昼休憩",
    desc: "現場近くで昼食・休憩",
  },
  {
    time: "13:00",
    title: "業務開始",
    desc: "午後の業務を再開します",
  },
  {
    time: "17:30",
    title: "業務終了",
    desc: "現場での業務を終了します",
  },
  {
    time: "18:00",
    title: "片付け・退勤",
    desc: "片付けや着替えを終えて退勤",
  },
];

const highwayTimeline = [
  {
    time: "19:00",
    title: "事務所に出勤",
    desc: "難波事務所に集合し、準備を整えます",
  },
  {
    time: "20:00",
    title: "高速道路事務所にて朝礼・安全確認",
    desc: "チーム全員で本日の作業計画を確認し、安全確認を実施します",
  },
  {
    time: "21:00",
    title: "各ICにて出入口の封鎖業務開始",
    desc: "高速道路の出入口を封鎖し、工事現場の安全を確保します。※休憩は1〜2時間に一度30分程度あり、トータル3〜4時間の休憩",
  },
  {
    time: "4:00",
    title: "作業終了",
    desc: "現場での封鎖業務を終了します",
  },
  {
    time: "5:00",
    title: "事務所に戻り退勤",
    desc: "事務所に戻り、片付けをして退勤。業務が早く終了しても1日分の給与保証があります",
  },
];

function TimelineBlock({ timeline }: { timeline: typeof constructionTimeline }) {
  return (
    <div className="relative pl-8 md:pl-10">
      <div className="absolute left-2.5 md:left-3 top-2 bottom-2 w-0.5 bg-gold/30" />
      <div className="space-y-6 md:space-y-8">
        {timeline.map((item, i) => (
          <div key={i} className="relative">
            <div className="absolute left-[-26px] md:left-[-30px] top-1 w-5 h-5 rounded-full bg-gold border-2 border-white shadow flex items-center justify-center z-10">
              <div className="w-2 h-2 rounded-full bg-white" />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
              <span className="text-gold font-black text-lg md:text-xl min-w-[4rem]">{item.time}</span>
              <div>
                <h4 className="text-navy font-bold text-base md:text-lg">{item.title}</h4>
                <p className="text-gray-600 text-sm mt-0.5">{item.desc}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RecruitDayInLifeSection() {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>(0.1);

  return (
    <section className="bg-navy/5 py-16 md:py-24" id="day-in-life">
      <div className="w-full px-4 md:px-8 max-w-6xl mx-auto" ref={ref}>
        <div
          className={`transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <SectionTitle en="A DAY IN LIFE" ja="1日の流れ" />

          {/* ①工事現場の例 */}
          <div className="mb-16 md:mb-20">
            <h3 className="text-lg md:text-xl font-bold text-navy mb-6 text-center">
              工事現場の例
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10">
              <div className="lg:col-span-2">
                <div className="h-72 md:h-96 lg:h-full rounded-2xl overflow-hidden">
                  <img
                    src="/images/GRANSTARK-recruite.html_day-in-life_section_construction_image.jpg"
                    alt="交通誘導警備スタッフの1日の流れ"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="lg:col-span-3">
                <TimelineBlock timeline={constructionTimeline} />
              </div>
            </div>
          </div>

          {/* ②高速道路警備の例 */}
          <div>
            <h3 className="text-lg md:text-xl font-bold text-navy mb-6 text-center">
              高速道路警備の例
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10">
              <div className="lg:col-span-2">
                <div className="h-72 md:h-96 lg:h-full rounded-2xl overflow-hidden">
                  <img
                    src="/images/GRANSTARK-recruite.html_day-in-life_section_image.jpg"
                    alt="高速道路警備スタッフの1日の流れ"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="lg:col-span-3">
                <TimelineBlock timeline={highwayTimeline} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
